import { useCallback, useEffect, useRef, useState } from "react";

import { extractHttpStatus } from "@/utilities/extractHttpStatus";
import { isValidProposalId } from "@/utilities/proposalId";

type DetailsState<T> = {
  id: number | undefined;
  proposalData: T | null;
  loading: boolean;
  notFound: boolean;
  loadError: boolean;
};

const initialState = <T,>(id: number | undefined): DetailsState<T> => ({
  id,
  proposalData: null,
  loading: isValidProposalId(id),
  notFound: !isValidProposalId(id),
  loadError: false,
});

/**
 * 詳細の状態はID単位で管理する。取得済みデータの保持は同じIDの一時障害に限る。
 * fetchProposalは呼び出し側でuseCallbackにより安定させる。
 */
export const useProposalDetails = <T,>(
  id: number | undefined,
  fetchProposal: (id: number) => Promise<{ status: number; data: T }>
) => {
  const [state, setState] = useState<DetailsState<T>>(() => initialState<T>(id));
  const activeId = useRef(id);
  const mounted = useRef(false);
  const requestVersion = useRef(0);

  const refetch = useCallback(async () => {
    // 古い画面の承認処理などから遅れて呼ばれても、新しいIDへ影響させない。
    if (!mounted.current || activeId.current !== id) return;
    const version = ++requestVersion.current;
    if (!isValidProposalId(id)) {
      setState(initialState<T>(id));
      return;
    }
    setState((previous) => ({
      ...(previous.id === id ? previous : initialState<T>(id)),
      loading: true,
      notFound: false,
      loadError: false,
    }));
    const isCurrent = () => mounted.current && activeId.current === id && requestVersion.current === version;
    try {
      const response = await fetchProposal(id);
      if (!isCurrent()) return;
      // 通常はaxiosが非2xxをrejectする。空レスポンス等も成功した詳細として扱わない。
      if (response.status !== 200 || !response.data) {
        throw Object.assign(new Error("Invalid proposal response"), { response: { status: response.status } });
      }
      setState({ id, proposalData: response.data, loading: false, notFound: false, loadError: false });
    } catch (error) {
      if (!isCurrent()) return;
      const notFound = extractHttpStatus(error) === 404;
      setState((previous) => ({
        id,
        proposalData: notFound || previous.id !== id ? null : previous.proposalData,
        loading: false,
        notFound,
        loadError: !notFound,
      }));
      // HTTPエラーの報告はCustomApiFactoryに集約する（console.errorによる二重送信を避ける）。
    }
  }, [id, fetchProposal]);

  useEffect(() => {
    mounted.current = true;
    activeId.current = id;
    void refetch();
    return () => {
      mounted.current = false;
      ++requestVersion.current;
    };
  }, [id, refetch]);

  // effectが動く前の描画でも、前のIDの本文やエラーを公開しない。
  const current = state.id === id ? state : initialState<T>(id);
  return { ...current, refetch };
};
